import React, {FunctionComponent, useEffect, useState} from 'react';
import IconButton from "@mui/material/IconButton";
import {useAppDispatch, useAppSelector} from '../../../../Config/Hooks';
import {RootState} from '../../../../Config/Store';
import {getEntities, reset, TemperatureMeasurement} from './TransformerTemperatureReducer';
import {truncateNumber} from "../../../../Utils/NumberUtil";
import {
    ACard,
    AChartX,
    Period,
    PeriodSelector,
    Skeleton,
    XAxis
} from "@atiautomacao/ati-ui-library";
import {format} from "date-fns";
import {useSnackbar} from "notistack";
import {checkDiffDays} from "../../../../Utils/DataUitils";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import useInterval from "../../../../Shared/Hooks/useInterval";
import {LocalDateTimeFormatISO} from "../../../../Utils/DateFormatPatternUtils";
import {downloadFilePostMapping} from "../../../../Utils/DonwloadUtilsForPostMapping";
import {hasPermission} from "../../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES} from "../../../../Config/Constants";
import {Box, Divider, Stack, Typography, useTheme} from "@mui/material";
import { SimCardDownloadOutlined } from '@mui/icons-material';

export interface SeriesOption {
    name: string;
    type: string;
    showSymbol?: boolean;
    smooth?: boolean;
    data: any[];
}

const TransformerTemperatureChart: FunctionComponent<{powerPlant: number, skidId: number}> = ({powerPlant, skidId}) => {
    const theme = useTheme();
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSearchByCsv = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_CSV_EXPORT, AUTHORITIES.EXPORT_TRANSFORMER_TEMPERATURE, AUTHORITIES.CSV_EXPORT_TRANSFORMER_TEMPERATURE
    ]);
    const isAuthorizedToSearchByExcel = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_CSV_EXPORT, AUTHORITIES.EXPORT_TRANSFORMER_TEMPERATURE, AUTHORITIES.EXCEL_EXPORT_TRANSFORMER_TEMPERATURE
    ]);
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const entities:Array<TemperatureMeasurement> = useAppSelector((state: RootState) => state.transformerTemperatureChart.entities);
    const { loading } = useAppSelector((state: RootState) => state.transformerTemperatureChart);
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    
    // Setting range start period
    const initialPeriod : any = {
        groupBy : "week",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };
    const [equipment, setEquipment] = useState<Array<TemperatureMeasurement>>(new Array<TemperatureMeasurement>)
    const [period, setPeriod] = useState<Period>(initialPeriod);
    const [xAxis, setXAxis] = useState<XAxis>();
    const [series, setSeries] = useState<Array<SeriesOption>>(new Array<SeriesOption>);

    const callback = (params:any) => {
        let formatDate = period && period.groupBy.valueOf() === "week" ? 'dd/MM HH:mm' : 'HH:mm'
        let dateStr = format(new Date(params[0].axisValue), formatDate);

        let result = dateStr + '<br/>';
        params.forEach(function (item: any) {
            result += item.marker + " " + item.seriesName + "&nbsp;&nbsp;&nbsp;&nbsp;" + "<strong>" + item.value[1] + " " + "°C" +"</strong>" + "<br/>";
        });

        return result;
    }
    const isDay = ()=>{
        return period && period.groupBy.valueOf() === "day"
    }
    const sortDataArray = (dataArray:Array<any>): any => {
        return dataArray.sort((a, b) => {
            const dateA = new Date(a[0]);
            const dateB = new Date(b[0]);
            return dateA.getTime() - dateB.getTime();
        });
    }


    const max = () => {
        let values: number[] = [];
        if(equipment){
            equipment.forEach(item =>
                item.value.forEach(element => {
                    if(element != null) values.push(truncateNumber(element));
                })
            )
        }
        if(values.length === 0) return 0;
        const valueMax = Math.max(...values);
        return truncateNumber(valueMax + (valueMax * 0.15));
    }

    // return the group
    const handleOnGroupByTypeOfPeriod = (valueTypePeriod: string) => {
        if(valueTypePeriod.includes('day')){
            return 'Horário';
        } else if(valueTypePeriod.includes('week')) {
            return 'Dia';
        }
    }

    const minDate = (minDate:string) : string | undefined => {
        if(!isDay() || !minDate) return undefined;
        const min = new Date(minDate);
        min.setHours(4, 0, 0, 0); // 4:00
        return format(min, 'yyyy-MM-dd HH:mm:ss.S')
    }

    const maxDate = (maxDate:string) : string | undefined => {
        if(!isDay() || !maxDate) return undefined;
        const max = new Date(maxDate);
        max.setHours(20, 0, 0, 0);
        return format(max, 'yyyy-MM-dd HH:mm:ss.S')
    }

    const hoursToMilliseconds = (hours:number): number => {
        return hours * 60 * 60 * 1000
    };

    const findTransformerTemperatureDataByPeriod = () => {
        const equipmentWithData = equipment.filter(item =>
            item.dateTime && item.dateTime.length > 0 && item.value.some(value => value != null)
        );

        if(equipmentWithData.length === 0){
            setXAxis(undefined);
            setSeries([]);
            return;
        }

        if(period.groupBy){
            setXAxis({
                name: handleOnGroupByTypeOfPeriod(period.groupBy),
                type: 'time',
                minInterval: period.groupBy.valueOf() === "day" ? hoursToMilliseconds(3) : hoursToMilliseconds(24),
                min: minDate(equipmentWithData[0].dateTime[0]),
                max:maxDate(equipmentWithData[0].dateTime[0]),
                axisLabel: {
                    formatter: period.groupBy.valueOf() === "day" ? '{HH}:{mm}' : '{dd}/{MM}'
                },
                boundaryGap: true,
                nameLocation: "middle",
                nameGap: 25
            });
        }
        let dataSeries: Array<SeriesOption> = [];

        equipmentWithData.forEach((item) => {
            const dataArray = item.dateTime.map((date, index) => item.value[index] != null ? [date, truncateNumber(item.value[index], 2)] : [date,'-'])
            const newDataArray = sortDataArray(dataArray)
            dataSeries.push({
                    name: `Relé de Trafo - ` + item.teleObjectConfigName.split(" ").slice(-2).join(" "),
                    data: newDataArray,
                    type: 'line',
                    smooth: true
                }
            )
        })
        setSeries(dataSeries)
    }

    useEffect(() => {
        dispatch(reset())
        setSeries([])
        setEquipment([])
    },[skidId])

    useEffect(() => {
        if(period && checkDiffDays(period.toDateTime, period.fromDateTime)){
            dispatch(getEntities({period: period, powerPlant: powerPlant, skidId: skidId}))
        }
    },[period, skidId])

    useInterval(() => {
        if(period && checkDiffDays(period.toDateTime, period.fromDateTime)){
            dispatch(getEntities({period: period, powerPlant: powerPlant, skidId: skidId}))
        }
    }, userConfig.mainUpdateInterval) // 5 minutes

    useEffect(() => {
        if(entities && entities.length > 0){
            setEquipment(entities)
        }else{
            setEquipment([])
        }
    }, [entities]);

    useEffect(() => {
        if(equipment && equipment.length > 0){
            findTransformerTemperatureDataByPeriod()
        }else {
            dispatch(reset())
            setSeries([])
        }
    },[equipment])

    const handleCsvDownload = () => {
        let apiUrl = 'api/report/tele-objects/csv';

        const params = {
            teleObjectsId: equipment?.map(item => item.teleObjectId),
            powerStationsId: [powerPlant],
            skidIds: [skidId],
            startDateTime: LocalDateTimeFormatISO(period?.fromDateTime),
            endDateTime: LocalDateTimeFormatISO(period?.toDateTime),
            grouped: "notGrouped",
        };

        downloadFilePostMapping(apiUrl, params, 'relatorio_telemedidas.csv', () => {
                enqueueSnackbar("Sucesso ao baixar o CSV", {variant: 'success'});
            },
            () => {
                enqueueSnackbar("Erro ao baixar o arquivo, tente mudar o Range de datas!", {variant: "error"});
            }
        );

    }

    const handleExcelDownload = () => {

        let apiUrl = 'api/report/tele-objects/excel';

        const params = {
            teleObjectsId: equipment?.map(item => item.teleObjectId),
            powerStationsId: [powerPlant],
            skidIds: [skidId],
            startDateTime: LocalDateTimeFormatISO(period?.fromDateTime),
            endDateTime: LocalDateTimeFormatISO(period?.toDateTime),
            grouped: "notGrouped",
        };

        downloadFilePostMapping(apiUrl, params, 'relatorio_telemedidas.xlsx', () => {
                enqueueSnackbar("Sucesso ao Baixar o Excel", {variant: 'success'})
            },
            () => {
                enqueueSnackbar("Erro ao baixar o arquivo, tente mudar o Range de datas!", {variant: "error"});
            }
        );
    }

    // @ts-ignore
    const option: AChartXProps['option'] = {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    formatter: function (params: any) {
                        const formatDate = period.groupBy.valueOf() === "week" ? "dd/MM" : "HH:mm"

                        if (params.axisDimension === 'x') {
                            return format(new Date(params.value), formatDate);
                        }
                        if (params.axisDimension === 'y') {
                            return truncateNumber(params.value,2);
                        }
                    }
                }
            },
            formatter: callback
        },
        toolbox: {
            itemSize: 15,
            feature: {
                dataZoom: {
                    yAxisIndex: 'none'
                },
                restore: {},
                saveAsImage: {
                    show: true,
                    name: `temperatura-do-transformador-${format(new Date(new Date()), 'yyyy-MM-dd')}`,
                },
            },
            orient: "vertical",
        },
        dataZoom: [
            {
                type: 'inside',
                throttle: 50
            },
        ],
        grid: {
            containLabel: true,
            top: 10,
            bottom: 45,
            right: 50,
            left: 0,
        },
        legend: {
            type:'scroll',
            orient: "horizontal",
            left: 'center',
            right: '10%',
            bottom: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
        },
        xAxis: xAxis,
        yAxis:
            {
                type: 'value',
                name: '°C',
                max: max(),
                interval: truncateNumber(max()/4),
                nameLocation: "middle",
                nameGap: 60,
                min: 0,
            }
        ,
        series: series
    }

    return (
        <ACard
            key={`chart-skid-transformer`}
            headerControlsPosition={"header"}
            styleProps={{
                cardStyle: { height: 463 },
                contentStyle: {padding: '0 16px', height: 408 },
                subTitleStyle: { display: 'none'},
            }}
            title={"Temperatura do Transformador"}
            ariaLabel="dashboard-skid-transformer-temperature-chart"
            headerControls={
         
                    <PeriodSelector
                        styleProps={{base: {flexDirection: "row", display: "flex", alignItems: "center", padding: '8px 0px 8px 0px'}}}
                        mode="hybrid"
                        hideDatetime={false}
                        hideGroup={false}
                        disableHourSelector={true}
                        inputFormat={'dd/MM/yyyy'}
                        periodInterval={{
                            day: {date: period.toDateTime, startTime: "00:00:00", endTime: "23:59:59"},
                            week: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 6}
                        }}
                        disabled={loading}
                        period={period}
                        onChange={setPeriod}
                        groupByOptions={["day", "week"]}
                    />
            }
            headerActions={
                <Stack direction="column" spacing={"8px"}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px' }}>
                        <IconButton disabled={!isAuthorizedToSearchByCsv} size="small" sx={{borderRadius: '4px'}} onClick={handleCsvDownload} >
                            <SimCardDownloadOutlined sx={{ marginRight: '12px' }} />
                            <Typography variant="body2">Baixar como CSV</Typography>
                        </IconButton>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px' }}>
                        <IconButton disabled={!isAuthorizedToSearchByExcel} size="small" sx={{borderRadius: '4px'}} onClick={handleExcelDownload} >
                            <SimCardDownloadOutlined sx={{ marginRight: '12px' }} />
                            <Typography variant="body2">Baixar como Excel</Typography>
                        </IconButton>
                    </Box>
                    <Divider />
                </Stack>
            }
        >
            {
                loading ?
                    <Skeleton animation="wave" height={363} variant="rounded" width={"100%"} />
                    :
                    (equipment && equipment.length > 0 && series && series.length > 0 && xAxis) ?
                        <AChartX
                            key={`chart-skid-transformer-${openSubMenu}`}
                            option={option}
                            height={363}
                            width={"100%"}
                            loading={false}
                            theme={theme.palette.mode}
                        />
                        :
                        <DataNotFound boxStyle={{height: 363, width: '100%'}}/>
            }
        </ACard>
    )
}

export default TransformerTemperatureChart;